import { useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { supabase } from "@/lib/agents/db"

interface MFAChallengeProps {
  challengeId: string
  factors: Array<{
    id: string
    factor_type: string
    friendly_name?: string
    phone?: string
  }>
  onSuccess: () => void
  onCancel: () => void
}

export function MFAChallenge({ challengeId, factors, onSuccess, onCancel }: MFAChallengeProps) {
  const [code, setCode] = useState("")
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  const selectedFactor = factors[0]

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    if (!selectedFactor) {
      toast({
        title: "Error",
        description: "No factor selected",
        variant: "destructive",
      })
      setLoading(false)
      return
    }

    try {
      const { data, error } = await supabase.auth.mfa.verify({
        challengeId,
        factorId: selectedFactor.id,
        code,
      })

      if (error) {
        toast({
          title: "Error",
          description: error.message || "Invalid verification code",
          variant: "destructive",
        })
        return
      }

      await supabase.auth.refreshSession()
      onSuccess()
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  if (!selectedFactor) {
    return null
  }

  const isPhone = selectedFactor.factor_type === "phone"

  return (
    <Card className="w-[350px] bg-white/95 backdrop-blur-md border-2 border-gray-200/50 shadow-2xl shadow-black/40">
      <CardHeader>
        <div className="flex justify-center mb-4">
          <img
            src="/signpost ai logo black@4x (1) (5).png"
            alt="SignpostAI Logo"
            className="h-12 w-auto"
          />
        </div>
        <CardTitle>Two-Factor Authentication</CardTitle>
        <CardDescription>
          {isPhone
            ? `Enter the verification code sent to ${selectedFactor.phone}`
            : "Enter the code from your authenticator app"}
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleVerify}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="code">Verification Code</Label>
            <Input
              id="code"
              type="text"
              placeholder={isPhone ? "Enter 6-digit code" : "Enter code from app"}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
              maxLength={6}
              autoFocus
            />
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <Button type="submit" className="w-full" disabled={loading || code.length < 6}>
            {loading ? "Verifying..." : "Verify"}
          </Button>
          <Button
            type="button"
            variant="link"
            className="w-full"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </CardFooter>
      </form>
    </Card>
  )
}

